import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Persistent anonymous session id — used by e-Tailor (and other anon-friendly flows)
// to scope per-browser data without requiring sign-up.
function getOrCreateSessionId() {
  if (typeof window === 'undefined') return '';
  try {
    let sid = window.localStorage.getItem('iariya_session_id');
    if (!sid) {
      // crypto.randomUUID isn't available on older Safari — fallback OK
      sid = (window.crypto?.randomUUID?.() || `sess-${Math.random().toString(36).slice(2)}-${Date.now()}`);
      window.localStorage.setItem('iariya_session_id', sid);
    }
    return sid;
  } catch {
    return `sess-${Date.now()}`;
  }
}

const api = axios.create({
  baseURL: API,
  timeout: 15000,
});

// Attach the anonymous session id to every request — backend reads it via X-Session-Id
api.interceptors.request.use((config) => {
  const sid = getOrCreateSessionId();
  if (sid) {
    config.headers = { ...(config.headers || {}), 'X-Session-Id': sid };
  }
  return config;
});

export const getSessionId = getOrCreateSessionId;

// iter128 — Platform webhooks (admin → API)
export const getWebhookEvents = () => api.get('/admin/webhooks/events');
export const listWebhooks = () => api.get('/admin/webhooks');
export const createWebhook = (payload) => api.post('/admin/webhooks', payload);
export const updateWebhook = (id, payload) => api.patch(`/admin/webhooks/${id}`, payload);
export const deleteWebhook = (id) => api.delete(`/admin/webhooks/${id}`);
export const testWebhook = (id) => api.post(`/admin/webhooks/${id}/test`);
export const getWebhookDeliveries = (id) => api.get(`/admin/webhooks/${id}/deliveries`);
export const rotateWebhookSecret = (id) => api.post(`/admin/webhooks/${id}/rotate-secret`);

// iter129 — Site Pages CMS (admin) + public page fetch
export const listCmsPages = () => api.get('/admin/cms/pages');
export const getCmsPage = (id) => api.get(`/admin/cms/pages/${id}`);
export const createCmsPage = (payload) => api.post('/admin/cms/pages', payload);
export const updateCmsPage = (id, payload) => api.patch(`/admin/cms/pages/${id}`, payload);
export const deleteCmsPage = (id) => api.delete(`/admin/cms/pages/${id}`);
export const getPublicPage = (slug) => api.get(`/pages/${slug}`);
export const getFooterPages = () => api.get('/pages');

// iter127 — surface the KYC document gate consistently across the app. When a
// partner attempts a transaction (publish a listing, etc.) without the
// mandatory CAC + Government ID, the backend returns 403 kyc_documents_required.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (
      error?.response?.status === 403 &&
      detail && typeof detail === 'object' &&
      detail.error === 'kyc_documents_required'
    ) {
      toast.error(detail.message || 'Upload your CAC and Government ID to continue.', {
        duration: 9000,
        description: 'Required for review before you can go live.',
        action: {
          label: 'Upload documents',
          onClick: () => { window.location.href = detail.upload_url || '/dashboard'; },
        },
      });
    }
    return Promise.reject(error);
  }
);

// Events
export const getEvents = (params) => api.get('/events', { params });
export const getEventDetail = (slug) => api.get(`/events/${slug}`);
export const createEvent = (data) => api.post('/events', data);

// Services
export const getServices = (params) => api.get('/services', { params });
export const getServiceDetail = (slug) => api.get(`/services/${slug}`);
export const createService = (data) => api.post('/services', data);

// Venues
export const getVenues = (params) => api.get('/venues', { params });
export const getVenueDetail = (slug) => api.get(`/venues/${slug}`);
export const createVenue = (data) => api.post('/venues', data);
export const requestVenueBooking = (slug, data) => api.post(`/venues/${slug}/book`, data);
export const getVenueAvailability = (slug, params = {}) => api.get(`/venues/${slug}/availability`, { params });

// Gallery
export const getGallery = (params) => api.get('/gallery', { params });

// Testimonials
export const getTestimonials = () => api.get('/testimonials');

// Contact
export const submitContact = (data) => api.post('/contact', data);

// Newsletter
export const subscribeNewsletter = (data) => api.post('/newsletter', data);

// Categories & Filters
export const getEventCategories = () => api.get('/categories/events');
export const getServiceCategories = () => api.get('/categories/services');
export const getVenueCategories = () => api.get('/categories/venues');
export const getStates = () => api.get('/filters/states');
export const getCities = (state) => api.get('/filters/cities', { params: { state } });
export const getAmenities = () => api.get('/filters/amenities');
export const getStats = () => api.get('/stats');

// Stays
export const getStays = (params) => api.get('/stays', { params });
export const getStayDetail = (slug) => api.get(`/stays/${slug}`);
export const getStayTypes = () => api.get('/categories/stays');

// Shop
export const getProducts = (params) => api.get('/shop', { params });
export const getProductDetail = (slug) => api.get(`/shop/${slug}`);
export const getShopCategories = () => api.get('/categories/shop');
export const createOrder = (data) => api.post('/orders', data);

// Sprint 2 — Cart + Checkout + Address Book + Order lifecycle
export const checkoutV2 = (data) => api.post('/orders/v2', data);
export const getMyOrdersV2 = () => api.get('/orders/v2/my-orders');
export const getOrderDetailV2 = (id) => api.get(`/orders/v2/${id}`);
export const advanceOrderStatusV2 = (id, data) => api.patch(`/orders/v2/${id}/status`, data);
export const listAddresses = () => api.get('/me/addresses');
export const createAddress = (data) => api.post('/me/addresses', data);
export const updateAddress = (id, data) => api.put(`/me/addresses/${id}`, data);
export const deleteAddress = (id) => api.delete(`/me/addresses/${id}`);
export const getSellerOrders = (params) => api.get('/shop/sellers/me/orders', { params });
// iter36 — event-native commerce moats
export const getMyVenueBookingsShippable = () => api.get('/me/venue-bookings-shippable');
export const getVatReport = (params) => api.get('/admin/vat/report', { params });
// iter37 — Platform admin settings + Group orders (event bulk-buy)
export const getPublicSettings = () => api.get('/settings/public');
export const getAdminSettings = () => api.get('/admin/settings');
export const updateAdminInsurance = (data) => api.put('/admin/settings/insurance', data);
export const updateAdminVat = (data) => api.put('/admin/settings/vat', data);
export const updatePlannerNudges = (data) => api.put('/admin/settings/planner-nudges', data);
export const createGroupOrder = (data) => api.post('/group-orders', data);
export const getGroupOrder = (slug) => api.get(`/group-orders/${slug}`);
export const joinGroupOrder = (slug, data) => api.post(`/group-orders/${slug}/join`, data);
export const attachOrderToGroup = (slug, data) => api.post(`/group-orders/${slug}/attach-order`, data);
export const listMyGroupOrders = () => api.get('/group-orders/me/list');
export const closeGroupOrder = (slug) => api.patch(`/group-orders/${slug}/close`);

// Seed
export const seedDatabase = () => api.post('/seed');

// Auth
export const registerUser = (data) => api.post('/auth/register', data);
export const loginUser = (data) => api.post('/auth/login', data);
// iter137 — account verification (email magic-link)
export const verifyEmailToken = (token) => api.post('/auth/verify-email', { token });
export const resendVerification = (email) => api.post('/auth/resend-verification', { email });
export const getMe = () => api.get('/auth/me');
export const getMyEvents = () => api.get('/auth/my-events');
export const getMyServices = () => api.get('/auth/my-services');
export const getMyVenues = () => api.get('/auth/my-venues');
export const getMyStays = () => api.get('/auth/my-stays');
export const getMyInquiries = () => api.get('/auth/inquiries');
export const getMyPurchases = () => api.get('/auth/my-purchases');
export const getUserPermissions = () => api.get('/auth/permissions');
export const createInquiry = (data) => api.post('/auth/inquiries', data);

// Admin User Management
export const getAdminUsers = () => api.get('/auth/admin/users');
export const changeUserRole = (userId, role) => api.put(`/auth/admin/users/${userId}/role`, { role });
export const changeUserStatus = (userId, active) => api.put(`/auth/admin/users/${userId}/status`, { active });
export const deleteUser = (userId) => api.delete(`/auth/admin/users/${userId}`);
export const getDeleteUserPreview = (userId) => api.get(`/auth/admin/users/${userId}/delete-preview`);

// Admin Event & Transaction Tracking (iter136)
export const getAdminTrackingSummary = () => api.get('/admin/tracking/summary');
export const getAdminTrackingEvents = (params) => api.get('/admin/tracking/events', { params });
export const getAdminTrackingTransactions = (params) => api.get('/admin/tracking/transactions', { params });
export const getAdminTrackingTransaction = (ref) => api.get(`/admin/tracking/transactions/${encodeURIComponent(ref)}`);

// Geocoding backfill (iter138)
export const getGeocodeStatus = () => api.get('/admin/geocode/status');
export const runGeocodeBackfill = (collection, limit = 200) => api.post('/admin/geocode/backfill', { collection, limit });
export const runGeocodeBackfillBookings = (collection, limit = 300) => api.post('/admin/geocode/backfill-bookings', { collection, limit });
export const getAdminStats = () => api.get('/auth/admin/stats');

// Commission & Revenue
export const getCommissionConfig = () => api.get('/commission/config');
export const updateCommissionConfig = (data) => api.put('/commission/config', data);
export const getPartnerDashboard = () => api.get('/commission/my-dashboard');
export const getAdminRevenue = () => api.get('/commission/admin/revenue');
export const recordTransaction = (data) => api.post('/commission/record', data);
export const seedCommission = () => api.post('/commission/seed');

// Email (admin)
export const getEmailConfig = () => api.get('/email/config');
export const sendTestEmail = (to) => api.post('/email/test', { to });
export const getEmailLog = (params) => api.get('/email/log', { params });
export const getEmailStats = () => api.get('/email/stats');
// iter110 — provider + sender config (super-admin only)
export const updateEmailSettings = (payload) => api.put('/email/settings', payload);

// iter111 — Meeting Room Series (recurring bookings on one room)
export const listMeetingRoomSeries = (params) => api.get('/meeting-room-series', { params });
export const listSeriesForRoom = (roomIdOrSlug) =>
  api.get(`/meeting-rooms/${roomIdOrSlug}/series`);
export const getMeetingRoomSeries = (slug) => api.get(`/meeting-room-series/${slug}`);
export const createMeetingRoomSeries = (roomIdOrSlug, payload) =>
  api.post(`/meeting-rooms/${roomIdOrSlug}/series`, payload);
export const cancelMeetingRoomSeries = (id) => api.delete(`/meeting-room-series/${id}`);
export const getMeetingRoomMeta = () => api.get('/categories/meeting-rooms/amenities');

// iter112 — Meeting Room Series monetization (Phase D)
export const getSeriesPromotionPricing = () => api.get('/meeting-room-series-pricing');
export const promoteSeries = (slug, payload) =>
  api.post(`/meeting-room-series/${slug}/promote`, payload);
export const subscribeToSeries = (slug, payload) =>
  api.post(`/meeting-room-series/${slug}/subscribe`, payload);
export const listSeriesSubscribers = (slug) =>
  api.get(`/meeting-room-series/${slug}/subscribers`);
export const purchaseBlastQuota = (slug, payload) =>
  api.post(`/meeting-room-series/${slug}/email-blast/purchase`, payload);
export const sendSeriesBlast = (slug, payload) =>
  api.post(`/meeting-room-series/${slug}/email-blast/send`, payload);
export const getSeriesBlastHistory = (slug) =>
  api.get(`/meeting-room-series/${slug}/blasts`);

// AI Share Card
export const generateShareCard = (data) => api.post('/ai/generate-share-card', data);
export const updateListingShareImage = (kind, id, share_image) =>
  api.patch(`/ai/listing/${kind}/${id}/share-image`, { share_image });

// AI Venue Studio
export const generateVenueRenditions = (data) => api.post('/ai/venue-studio/generate', data);
export const saveVenueStudioBoard = (data) => api.post('/ai/venue-studio/boards', data);
export const listVenueStudioBoards = () => api.get('/ai/venue-studio/boards');
export const generateVenueShowcase = (venueId, regenerate = false) =>
  api.post(`/ai/venue-studio/showcase/${venueId}`, { regenerate });
export const promoteVenueShowcase = (venueId, data) =>
  api.post(`/ai/venue-studio/showcase/${venueId}/promote`, data);
export const getMyAICredits = () => api.get('/ai/credits/me');
// Referrals (iter51)
export const getMyReferralSummary = () => api.get('/referrals/me');
// Avatar / cover upload (iter54)
export const uploadMyAvatar = (data_url) => api.post('/auth/me/avatar', { data_url });
export const uploadMyCover = (data_url) => api.post('/auth/me/cover', { data_url });
// iter72 — Listing-scoped messaging consolidated into unified Conversations API
// (see createConversation / listConversations below). Legacy listing-thread
// endpoints removed.

// Event-Type landing pages (SEO)
export const getEventTypeLanding = (eventType) => api.get(`/event-types/${eventType}`);

// Site settings (footer contact + social + map)
export const getSiteSettings = () => api.get('/site-settings');
export const updateSiteSettings = (data) => api.put('/site-settings', data);

// Admin — AI Credits manager
export const adminGetAICreditsConfig = () => api.get('/admin/ai-credits/config');
export const adminUpdateAICreditsConfig = (data) => api.put('/admin/ai-credits/config', data);
export const adminListUserCredits = (params = {}) => api.get('/admin/ai-credits/users', { params });
export const adminGrantCredits = (data) => api.post('/admin/ai-credits/grant', data);
export const adminDeductCredits = (data) => api.post('/admin/ai-credits/deduct', data);
export const adminGetCreditsLedger = (params = {}) => api.get('/admin/ai-credits/ledger', { params });
export const adminGetCreditsSummary = () => api.get('/admin/ai-credits/summary');

// Admin — Pages, Subscriptions, Workflows
export const adminGetPages = () => api.get('/admin/pages');
export const adminListSubscriptions = (params = {}) => api.get('/admin/subscriptions', { params });
export const adminUpdateSubscription = (id, data) => api.patch(`/admin/subscriptions/${id}`, data);
export const adminWorkflowSummary = () => api.get('/admin/workflows/summary');
export const adminListKYC = (params = {}) => api.get('/admin/workflows/kyc', { params });
export const adminGetKYCDocuments = (userId) => api.get(`/admin/workflows/kyc/${userId}/documents`);
export const adminDecideKYC = (userId, data) => api.patch(`/admin/workflows/kyc/${userId}`, data);

// File Upload
export const uploadImage = (formData) => api.post('/upload/image', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
export const uploadDocument = (formData) => api.post('/upload/document', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
export const uploadVerificationDocuments = (documents) => api.post('/auth/upload-documents', { documents });
export const adminEditProfile = (userId, data) => api.put(`/admin/edit-profile/${userId}`, data);
export const adminEditListing = (data) => api.put('/admin/edit-listing', data);

// Profile management (self-serve + admin-on-behalf)
export const updateMyProfile = (data) => api.put('/auth/profile', data);
export const getMyProfile = () => api.get('/auth/me');
export const adminGetUser = (userId) => api.get(`/auth/admin/users/${userId}`);
export const adminUpdateUserProfile = (userId, data) => api.put(`/auth/admin/users/${userId}/profile`, data);
export const createPortfolioEntry = (data) => api.post('/portfolios', data);
export const getMyPortfolio = () => api.get('/portfolios/me');
export const getUserPortfolio = (userId) => api.get('/portfolios', { params: { owner_id: userId } });
export const updatePortfolioEntry = (id, data) => api.patch(`/portfolios/${id}`, data);
export const reorderPortfolioEntries = (orderedIds) => api.post('/portfolios/reorder', { order: orderedIds });
export const deletePortfolioEntry = (id) => api.delete(`/portfolios/${id}`);

// Reviews & Ratings
export const submitReview = (data) => api.post('/reviews-legacy', data);
export const getListingReviews = (type, slug) => api.get(`/reviews-legacy/${type}/${slug}`);
export const getProviderReviews = (userId) => api.get(`/providers/${userId}/reviews`);

// Public Provider Profile
export const getProviderProfile = (userId) => api.get(`/providers/${userId}`);
export const getProviderListings = (userId) => api.get(`/providers/${userId}/listings`);
// iter73 — Featured providers for the home page gallery (admin picks who appears)
export const getFeaturedProviders = (params = {}) => api.get('/providers/featured', { params });
export const getProvidersRatingsBatch = (userIds) => api.post('/providers/ratings/batch', { user_ids: userIds });

// Availability Calendar
export const getAvailability = (type, slug) => api.get(`/availability/${type}/${slug}`);
export const blockDates = (type, slug, dates) => api.post(`/availability/${type}/${slug}/block`, { dates });
export const unblockDates = (type, slug, dates) => api.post(`/availability/${type}/${slug}/unblock`, { dates });
export const checkDateAvailable = (type, slug, date) => api.get(`/availability/${type}/${slug}/check?date=${date}`);

// Set auth token for all requests
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

// e-Tailor AI
export const analyzeMeasurements = (formData) => api.post('/etailor/analyze', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
export const generateVisualization = (formData) => api.post('/etailor/visualize', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 });
export const generateComparison = (formData) => api.post('/etailor/visualize-compare', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
export const generateFromReference = (formData) => api.post('/etailor/visualize-from-reference', formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 240000 });
export const updateMeasurementsManual = (id, data) => api.put(`/etailor/measurements/${id}`, data);
export const getMeasurement = (id) => api.get(`/etailor/measurements/${id}`);

// Ticketing
export const getEventTickets = (eventSlug) => api.get(`/tickets/event/${eventSlug}`);
export const purchaseTicket = (data) => api.post('/tickets/purchase', data);
export const verifyTicket = (code) => api.get(`/tickets/verify/${code}`);
export const getMyTickets = (email) => api.get(`/tickets/my-tickets?email=${email}`);
export const getTicketTypes = () => api.get('/tickets/types');
export const createTicketType = (data) => api.post('/tickets/types', data);
export const deleteTicketType = (id) => api.delete(`/tickets/types/${id}`);
export const seedTickets = () => api.post('/tickets/seed');

// Guest List / RSVP
export const inviteGuest = (data) => api.post('/guests/invite', data);
export const bulkInviteGuests = (data) => api.post('/guests/bulk-invite', data);
export const getEventGuestList = (eventSlug) => api.get(`/guests/event/${eventSlug}`);
export const getRsvpPage = (inviteCode) => api.get(`/guests/rsvp/${inviteCode}`);
export const submitRsvp = (inviteCode, data) => api.post(`/guests/rsvp/${inviteCode}`, data);
export const removeGuest = (guestId) => api.delete(`/guests/${guestId}`);
export const sendGuestInvite = (inviteCode, data) => api.post(`/guests/send-invite/${inviteCode}`, data);
export const bulkSendInvites = (eventSlug, data) => api.post(`/guests/bulk-send/${eventSlug}`, data);

// iter80 — Event-day stakeholder check-in
export const issueVendorCheckinToken = (eventSlug, data) => api.post(`/events/${eventSlug}/vendor-checkin/issue`, data);
export const listVendorCheckinTokens = (eventSlug) => api.get(`/events/${eventSlug}/vendor-checkin`);
export const scanEventToken = (eventSlug, token) => api.post(`/events/${eventSlug}/scan`, { token });
export const getEventCheckinStats = (eventSlug) => api.get(`/events/${eventSlug}/checkin-stats`);

// iter81 — Aso-ebi link on event
export const linkEventAsoEbi = (eventSlug, data) => api.put(`/events/${eventSlug}/aso-ebi`, data);

// iter82 — Meals + seat labels + per-guest seat
export const setEventMealOptions = (eventSlug, options) => api.put(`/events/${eventSlug}/meal-options`, { options });
export const setEventSeatLabels = (eventSlug, seatLabels) => api.put(`/events/${eventSlug}/seat-labels`, { seat_labels: seatLabels });
export const assignGuestSeat = (inviteCode, seatLabel) => api.put(`/guests/${inviteCode}/seat`, { seat_label: seatLabel });
export const getEventMealRollup = (eventSlug) => api.get(`/events/${eventSlug}/meal-rollup`);

// iter83 — Per-event guest drip campaigns
export const getEventDrips = (eventSlug) => api.get(`/events/${eventSlug}/drips`);
export const updateEventDrip = (eventSlug, dripId, data) => api.put(`/events/${eventSlug}/drips/${dripId}`, data);
export const sendEventDripNow = (eventSlug, dripId) => api.post(`/events/${eventSlug}/drips/${dripId}/send-now`);

// Event Venue & Service Bookings
export const bookVenueForEvent = (data) => api.post('/event-bookings/venue', data);
export const bookServiceForEvent = (data) => api.post('/event-bookings/service', data);
export const getEventBookings = (eventSlug) => api.get(`/event-bookings/${eventSlug}`);
export const cancelEventBooking = (bookingId) => api.delete(`/event-bookings/${bookingId}`);

// Public service booking inquiry (with form + email notifications)
export const requestServiceBooking = (slug, data) => api.post(`/services/${slug}/book`, data);
export const getServiceInquiries = (slug) => api.get(`/services/${slug}/inquiries`);

// Stay Bookings
export const createBooking = (data) => api.post('/bookings', data);
export const getStayBookings = (staySlug) => api.get(`/bookings/stay/${staySlug}`);
export const updateBookingStatus = (bookingId, status) => api.put(`/bookings/${bookingId}/status`, { status });
export const getMyBookingsAsHost = () => api.get('/auth/my-bookings-host');
export const getMyBookingsAsGuest = () => api.get('/auth/my-bookings-guest');

// Payments
export const initializePayment = (data) => api.post('/payments/initialize', data);
export const verifyPayment = (reference, provider) => api.get(`/payments/verify/${reference}?provider=${provider}`);
export const finalizePayment = (reference) => api.post('/payments/finalize', { reference });
export const getPaymentConfig = () => api.get('/payments/config');
export const getPaymentByReference = (reference) => api.get(`/payments/reference/${reference}`);

// Messaging
export const listConversations = () => api.get('/messages/conversations');
export const getMessagesUnreadCount = () => api.get('/messages/unread-count');
export const createConversation = (data) => api.post('/messages/conversations', data);
export const getConversation = (id) => api.get(`/messages/conversations/${id}`);
export const getConversationMessages = (id, params) => api.get(`/messages/conversations/${id}/messages`, { params });
export const sendConversationMessage = (id, data) => api.post(`/messages/conversations/${id}/messages`, data);
export const markConversationRead = (id) => api.post(`/messages/conversations/${id}/read`);
export const searchMessageUsers = (q, limit = 10) => api.get('/messages/users/search', { params: { q, limit } });
export const resolveListingOwner = (type, slug) => api.get('/messages/resolve-owner', { params: { type, slug } });

export const openMessagesSocket = (token) => {
  if (!token) return null;
  const base = BACKEND_URL.replace(/^http/, 'ws');
  return new WebSocket(`${base}/api/messages/ws?token=${encodeURIComponent(token)}`);
};

// Monetization - Post Subscriptions (Phase 11)
export const getPostPlans = () => api.get('/post-subscriptions/plans');
export const getMyPostSubscription = () => api.get('/post-subscriptions/me');
export const subscribePostPlan = (tier, callback_url) => api.post('/post-subscriptions/subscribe', { tier, provider: 'flutterwave', callback_url });
export const verifyPostSubscription = (tx_ref) => api.get(`/post-subscriptions/verify/${tx_ref}`);
export const cancelPostSubscription = () => api.post('/post-subscriptions/cancel');

// Monetization - Ad Campaigns (Phase 12)
export const getAdPricing = () => api.get('/ad-campaigns/pricing');
export const submitAdCampaign = (data) => api.post('/ad-campaigns/submit', data);
export const trackAdCampaign = (id) => api.get(`/ad-campaigns/track/${id}`);
export const getActiveAdCampaigns = (placement, limit = 3) => api.get(`/ad-campaigns/active`, { params: { placement, limit } });
export const trackAdCampaignClick = (id) => api.post(`/ad-campaigns/${id}/click`);
export const adminListAdCampaigns = (status) => api.get('/admin/ad-campaigns', { params: { status } });
export const adminAdCampaignSummary = () => api.get('/admin/ad-campaigns/summary');
export const adminApproveAdCampaign = (id) => api.post(`/admin/ad-campaigns/${id}/approve`);
export const adminRejectAdCampaign = (id, reason) => api.post(`/admin/ad-campaigns/${id}/reject`, { reason });
export const adminPauseAdCampaign = (id) => api.post(`/admin/ad-campaigns/${id}/pause`);
export const adminResumeAdCampaign = (id) => api.post(`/admin/ad-campaigns/${id}/resume`);
export const adminDeleteAdCampaign = (id) => api.delete(`/admin/ad-campaigns/${id}`);
export const adminPostSubscriptionSummary = () => api.get('/admin/post-subscriptions/summary');

// AI Showcase backfill (admin)
export const adminStartShowcaseBackfill = (params = {}) =>
  api.post('/admin/venues/backfill-showcases', null, { params });
export const adminShowcaseBackfillStatus = () =>
  api.get('/admin/venues/backfill-showcases/status');

// Base64 → Object Storage migration (admin one-shot)
export const adminStartBase64Migration = (params = {}) =>
  api.post('/admin/storage/migrate-base64', null, { params });
export const adminBase64MigrationStatus = () =>
  api.get('/admin/storage/migrate-base64/status');

// e-Tailor promotion + analytics (admin)
export const adminGetEtailorPromotion = () => api.get('/admin/etailor/promotion');
export const adminStartEtailorPromotion = (days = 15, name = '') =>
  api.post('/admin/etailor/promotion/start', null, { params: { days, name } });
export const adminExtendEtailorPromotion = (days = 7) =>
  api.post('/admin/etailor/promotion/extend', null, { params: { days } });
export const adminStopEtailorPromotion = () =>
  api.post('/admin/etailor/promotion/stop');
export const adminEtailorAnalytics = (days = 30, only_promo = false) =>
  api.get('/admin/etailor/analytics', { params: { days, only_promo } });
export const adminEtailorRecentUses = (limit = 50) =>
  api.get('/admin/etailor/recent-uses', { params: { limit } });

// e-Tailor designs (gallery / email / download)
export const getMyEtailorDesigns = (sessionId = '') =>
  api.get('/etailor/my-designs', { params: sessionId ? { session_id: sessionId } : {}, headers: sessionId ? { 'X-Session-Id': sessionId } : {} });
export const getEtailorDesign = (designId) => api.get(`/etailor/designs/${designId}`);
export const emailEtailorDesign = (designId, email) =>
  api.post(`/etailor/designs/${designId}/email`, { email });

// e-Tailor marketing blast (admin)
export const adminEtailorAudiencePreview = (audience = 'all', recent_days = 30, sample = 6) =>
  api.get('/admin/etailor/marketing/audience-preview', { params: { audience, recent_days, sample } });
export const adminSendEtailorBlast = (payload) =>
  api.post('/admin/etailor/marketing/blast', payload);
export const adminEtailorBlastHistory = (limit = 25) =>
  api.get('/admin/etailor/marketing/history', { params: { limit } });

// Favorites
export const getFavorites = () => api.get('/favorites');
export const getFavoriteCount = () => api.get('/favorites/count');
export const addFavorite = (type, slug) => api.post('/favorites', { type, slug });
export const removeFavorite = (type, slug) => api.delete(`/favorites/${type}/${slug}`);
export const bulkSyncFavorites = (items) => api.post('/favorites/bulk-sync', { items });

// Shop Sellers (Sprint 1, Feb 2026)
export const onboardSeller = (data) => api.post('/shop/sellers/onboard', data);
export const getMySellerProfile = () => api.get('/shop/sellers/me');
export const updateMySellerProfile = (data) => api.put('/shop/sellers/me', data);
export const getPublicStorefront = (slug) => api.get(`/shop/sellers/${slug}`);
export const getMySellerProducts = () => api.get('/shop/sellers/me/products');
export const createSellerProduct = (data) => api.post('/shop/sellers/me/products', data);
export const updateSellerProduct = (id, data) => api.put(`/shop/sellers/me/products/${id}`, data);
export const deleteSellerProduct = (id) => api.delete(`/shop/sellers/me/products/${id}`);
export const getMySellerOrders = () => api.get('/shop/sellers/me/orders');
export const getMySellerPayouts = () => api.get('/shop/sellers/me/payouts');
// Storefront enhancements (iter57)
export const checkSellerSlugAvailable = (slug) => api.get('/shop/sellers/slug-available', { params: { slug } });
export const getSellerCsvTemplate = () => api.get('/shop/sellers/me/products/bulk-csv-template');
export const bulkImportSellerCsv = (formData, dryRun = false, aiCleanImages = false) =>
  api.post('/shop/sellers/me/products/bulk-csv', formData, {
    params: { dry_run: dryRun, ai_clean_images: aiCleanImages },
    headers: { 'Content-Type': 'multipart/form-data' },
  });

// iter58 — Sprint 3: shipping rates, returns, packing slip, delivery OTP, widget, share kit
export const quoteShipping = (state, weight_kg, subtotal) =>
  api.get('/shipping/quote', { params: { state, weight_kg, subtotal } });
export const adminGetShippingRates = () => api.get('/admin/shipping/rates');
export const adminUpdateShippingRates = (data) => api.put('/admin/shipping/rates', data);
export const fileReturn = (orderId, data) => api.post(`/orders/v2/${orderId}/return`, data);
export const listMyReturns = () => api.get('/returns/me');
export const getReturn = (id) => api.get(`/returns/${id}`);
export const decideReturn = (id, data) => api.patch(`/returns/${id}`, data);
export const triggerRefund = (id) => api.post(`/returns/${id}/refund`);
export const downloadPackingSlip = (orderId) =>
  api.get(`/orders/v2/${orderId}/packing-slip`, { responseType: 'blob' });
export const issueDeliveryOtp = (orderId, data) =>
  api.post(`/orders/v2/${orderId}/delivery-otp/issue`, data);
export const verifyDeliveryOtp = (orderId, otp) =>
  api.post(`/orders/v2/${orderId}/delivery-otp/verify`, { otp });
export const getMyShareKit = () => api.get('/shop/sellers/me/share-kit');

// iter59 — multi-currency FX + bulk waybill + AI autocategorise toggle
export const getFxRates = () => api.get('/fx/rates');
export const adminUpdateFxRates = (data) => api.put('/admin/fx/rates', data);
export const downloadBulkWaybills = (orderIds) =>
  api.post('/orders/v2/bulk-packing-slips', { order_ids: orderIds }, { responseType: 'blob' });

// iter101 — Stripe removed. All diaspora payments now flow through Flutterwave Standard checkout (multi-currency).

// QA Test Harness — admin (iter38)
export const adminQaScenarios = () => api.get('/admin/qa/scenarios');
export const adminQaListKeys = () => api.get('/admin/qa/keys');
export const adminQaCreateKey = (name, notes = '') => api.post('/admin/qa/keys', { name, notes });
export const adminQaPatchKey = (name, patch) => api.patch(`/admin/qa/keys/${name}`, patch);
export const adminQaRevokeKey = (name) => api.delete(`/admin/qa/keys/${name}`);
export const adminQaRunBatch = (payload) =>
  api.post('/admin/qa/run/batch', payload, { timeout: 120000 });
export const adminQaListRuns = (page = 1, page_size = 25) =>
  api.get('/admin/qa/runs', { params: { page, page_size } });
export const adminQaGetRun = (run_id) => api.get(`/admin/qa/runs/${run_id}`);
export const adminQaRunsRetention = () => api.get('/admin/qa/runs/retention');
export const adminQaRunsCleanup = (retention_days = 7) =>
  api.post('/admin/qa/runs/cleanup', null, { params: { retention_days } });

// iter62 — AI Suite (Concierge / Studio / Hall) waitlist
export const submitAiSuiteWaitlist = (payload) =>
  api.post('/ai-suite/waitlist', payload);
export const getAiSuiteWaitlistStats = () =>
  api.get('/ai-suite/waitlist/stats');
export const adminListAiSuiteWaitlist = (params = {}) =>
  api.get('/ai-suite/waitlist', { params });

// iter63 — Service-booking deposit payment flow
export const initServiceBookingPayment = (slug, payload) =>
  api.post(`/services/${slug}/book/payment-init`, payload);
export const verifyServiceBookingDeposit = (slug, payload) =>
  api.post(`/services/${slug}/book/verify-deposit`, payload);

// iter66 — WhatsApp Cloud API (Meta)
export const submitWhatsAppOptIn = (payload) =>
  api.post('/wa/optin', payload);
export const submitWhatsAppOptOut = (payload) =>
  api.post('/wa/optout', payload);
export const adminGetWhatsAppStatus = () =>
  api.get('/admin/wa/status');
export const adminListWhatsAppOptins = (params = {}) =>
  api.get('/admin/wa/optins', { params });
export const adminListWhatsAppMessages = (params = {}) =>
  api.get('/admin/wa/messages', { params });
export const adminSendWhatsAppTemplate = (payload) =>
  api.post('/admin/wa/send-template', payload);
export const adminBroadcastWhatsApp = (payload) =>
  api.post('/admin/wa/broadcast', payload);
export const adminListWhatsAppBroadcasts = (params = {}) =>
  api.get('/admin/wa/broadcasts', { params });

// iter68 — Verified partner reviews
export const getReviews = (listingType, slug, params = {}) =>
  api.get(`/reviews/${listingType}/${slug}`, { params });
export const getReviewStats = (listingType, slug) =>
  api.get(`/reviews/${listingType}/${slug}/stats`);
export const checkReviewEligibility = (listingType, slug) =>
  api.get(`/reviews/${listingType}/${slug}/eligibility`);
export const createReview = (listingType, slug, payload) =>
  api.post(`/reviews/${listingType}/${slug}`, payload);
export const deleteReview = (id) => api.delete(`/reviews/${id}`);
export const adminListReviews = (params = {}) =>
  api.get('/admin/reviews', { params });
export const adminModerateReview = (id, payload) =>
  api.patch(`/admin/reviews/${id}`, payload);

// iter69 — Admin edit on partner's behalf
export const adminGetAuditLog = (params = {}) =>
  api.get('/auth/admin/audit-log', { params });

// iter71 — Gele tier discovery
export const getGeleStyles = () => api.get('/gele-styles');
export const getGeleTiers = (params = {}) => api.get('/gele-tiers', { params });

// iter99 — WhatsApp Catalog (seller self-service)
export const getWaCatalogConfig = () => api.get('/wa-catalog/sellers/me/config');
export const setWaCatalogConfig = (payload) => api.put('/wa-catalog/sellers/me/config', payload);
export const getWaCatalogStatus = () => api.get('/wa-catalog/sellers/me/status');
export const triggerWaCatalogSync = (force = false) =>
  api.post('/wa-catalog/sellers/me/sync', null, { params: { force } });
export const diagnoseWaCatalog = () => api.post('/wa-catalog/sellers/me/diagnose');
export const previewWaCatalogProduct = (productId) =>
  api.get(`/wa-catalog/sellers/me/products/${productId}/preview`);
export const syncOneWaCatalogProduct = (productId) =>
  api.post(`/wa-catalog/sellers/me/products/${productId}/sync`);
export const unsyncOneWaCatalogProduct = (productId) =>
  api.delete(`/wa-catalog/sellers/me/products/${productId}/sync`);

// iAriya WWH — Wellness & Wellbeing personalised health utilities (AI via Gemini 3 Flash)
export const wwhAi = (payload) => api.post('/wwh/ai', payload, { timeout: 120000 });
export const wwhHealth = () => api.get('/wwh/health');

export default api;
